export interface Snapshot {
  t: number;
  x: number;
  y: number;
  facing: number;
}

export interface InterpolatedPose {
  x: number;
  y: number;
  facing: number;
}

/**
 * Entity interpolation: render a remote player at `renderTime` (a little in the past) by linearly
 * interpolating between the two authoritative snapshots that straddle it. This turns a 20 Hz
 * authoritative feed into smooth 60 FPS motion (see ADR-0004).
 */
export function sampleBuffer(buffer: Snapshot[], renderTime: number): InterpolatedPose | null {
  if (buffer.length === 0) return null;

  const last = buffer[buffer.length - 1]!;
  // Render time is ahead of everything we have (no newer snapshot yet) → hold the latest.
  if (renderTime >= last.t) {
    return { x: last.x, y: last.y, facing: last.facing };
  }

  const first = buffer[0]!;
  if (renderTime <= first.t) {
    return { x: first.x, y: first.y, facing: first.facing };
  }

  for (let i = buffer.length - 1; i > 0; i--) {
    const b = buffer[i]!;
    const a = buffer[i - 1]!;
    if (renderTime >= a.t && renderTime <= b.t) {
      const span = b.t - a.t;
      const alpha = span > 0 ? (renderTime - a.t) / span : 0;
      return {
        x: a.x + (b.x - a.x) * alpha,
        y: a.y + (b.y - a.y) * alpha,
        facing: b.facing,
      };
    }
  }

  return { x: last.x, y: last.y, facing: last.facing };
}

/** Drop snapshots older than `maxAgeMs` before `now`, keeping at least the two most recent. */
export function trimBuffer(buffer: Snapshot[], now: number, maxAgeMs: number): void {
  while (buffer.length > 2 && now - buffer[0]!.t > maxAgeMs) {
    buffer.shift();
  }
}
